import { useState, useEffect, useRef, useCallback } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import {
  Vault, Upload, Trash, Download, PencilSimple, FloppyDisk, X, File as FileIcon, ArrowClockwise, ShieldCheck,
} from "@phosphor-icons/react";

/**
 * DocsVaultPage — per-owner private vault for compliance PDFs.
 * Route: /app/vault
 *
 * Backend: /api/vault/{upload,files,files/{id}/download,files/{id},stats,tags}
 * All requests carry the JWT via the `client` axios instance.
 */
export default function DocsVaultPage() {
  const [files, setFiles] = useState([]);
  const [byTag, setByTag] = useState({});
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState({ file_count: 0, total_bytes: 0, per_tag: {} });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadTag, setUploadTag] = useState("coi");
  const [uploadLabel, setUploadLabel] = useState("");
  const [filter, setFilter] = useState("all");
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, s, t] = await Promise.all([
        client.get("/vault/files"),
        client.get("/vault/stats"),
        client.get("/vault/tags"),
      ]);
      setFiles(f.data.files || []);
      setByTag(f.data.by_tag || {});
      setStats(s.data);
      setTags(t.data.tags || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load your vault. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10 MB.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", uploadLabel.trim());
      fd.append("tag", uploadTag);
      const { data } = await client.post("/vault/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded "${data.file.label}"`);
      setUploadLabel("");
      e.target.value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const downloadFile = async (f) => {
    try {
      const resp = await client.get(`/vault/files/${f.id}/download`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = f.original_filename || f.label || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed.");
    }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Delete "${f.label}"? You won't be able to download it again from the vault.`)) return;
    try {
      await client.delete(`/vault/files/${f.id}`);
      toast.success("File removed from vault");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed.");
    }
  };

  const renameFile = async (fileId, label, tag) => {
    try {
      await client.patch(`/vault/files/${fileId}`, { label, tag });
      toast.success("Saved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    }
  };

  const visibleFiles = filter === "all"
    ? files
    : files.filter((f) => f.tag === filter);

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 space-y-6" data-testid="vault-page">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Vault size={22} weight="duotone" className="text-[#FF5A1F]" />
            <h1 className="text-2xl font-semibold text-white tracking-tight">Docs Vault</h1>
          </div>
          <p className="text-sm text-neutral-500">
            Every compliance PDF in one place: COI, W-9, MC authority, IFTA quarterly, driver files.
            Only you can see or download these.
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black tracking-tight text-white" data-testid="vault-file-count">
            {stats.file_count}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
            {stats.file_count === 1 ? "File saved" : "Files saved"} · {formatBytes(stats.total_bytes)}
          </div>
        </div>
      </header>

      {/* Upload card */}
      <div className="card-surface p-5 rounded-lg border-2 border-[#FF5A1F]/30 bg-[#FF5A1F]/[0.04]" data-testid="vault-upload-card">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1">
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">
              Document type
            </label>
            <select value={uploadTag} onChange={(e) => setUploadTag(e.target.value)} data-testid="vault-upload-tag"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60">
              {tags.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">
              Label (optional)
            </label>
            <input type="text" value={uploadLabel} onChange={(e) => setUploadLabel(e.target.value)}
              placeholder="e.g., Q3 2026 COI · exp 12/15/2026"
              data-testid="vault-upload-label"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60" />
          </div>
          <div>
            <input ref={fileInputRef} type="file" onChange={onFilePicked}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.docx,.xlsx,.csv,.txt"
              className="hidden" data-testid="vault-upload-input" />
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
              data-testid="vault-upload-btn"
              className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold bg-[#FF5A1F] hover:bg-[#FF7038] text-black disabled:opacity-50 whitespace-nowrap">
              {uploading ? (
                <><ArrowClockwise size={14} className="animate-spin" /> Uploading…</>
              ) : (
                <><Upload size={14} weight="bold" /> Choose file to upload</>
              )}
            </button>
          </div>
        </div>
        <div className="text-[11px] text-neutral-500 mt-3 flex items-center gap-1.5">
          <ShieldCheck size={12} className="text-emerald-500" />
          Files are stored privately. Max 10 MB per file · PDF, images, DOCX, XLSX, CSV, TXT.
        </div>
      </div>

      {/* Filter chips */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="vault-filter-chips">
          <TagChip active={filter === "all"} onClick={() => setFilter("all")} label="All" count={stats.file_count} />
          {tags.filter((t) => (stats.per_tag[t.code] || 0) > 0).map((t) => (
            <TagChip key={t.code} active={filter === t.code} onClick={() => setFilter(t.code)}
              label={t.label} count={stats.per_tag[t.code] || 0} testid={`vault-filter-${t.code}`} />
          ))}
        </div>
      )}

      {/* File list */}
      {loading ? (
        <div className="text-neutral-500 text-sm py-12 text-center">Loading your vault…</div>
      ) : files.length === 0 ? (
        <EmptyState />
      ) : visibleFiles.length === 0 ? (
        <div className="text-neutral-500 text-sm py-8 text-center">
          No files with this tag yet. Upload one, or {" "}
          <button onClick={() => setFilter("all")} className="text-[#FF5A1F] hover:underline">show all</button>.
        </div>
      ) : (
        <div className="space-y-2" data-testid="vault-file-list">
          {visibleFiles.map((f) => (
            <FileRow key={f.id} file={f} tags={tags}
              onDownload={() => downloadFile(f)}
              onDelete={() => deleteFile(f)}
              onRename={(label, tag) => renameFile(f.id, label, tag)} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────── FileRow ─────────────────── */
function FileRow({ file, tags, onDownload, onDelete, onRename }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(file.label);
  const [tag, setTag] = useState(file.tag);

  const save = () => {
    if (!label.trim()) return;
    onRename(label.trim(), tag);
    setEditing(false);
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors p-4 flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid={`vault-file-row-${file.id}`}>
      <div className="w-10 h-10 rounded-lg bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center shrink-0">
        <FileIcon size={20} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex flex-col sm:flex-row gap-2">
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus
              onKeyDown={(e) => e.key === "Enter" && save()}
              data-testid={`vault-rename-label-${file.id}`}
              className="flex-1 bg-white/[0.05] border border-[#FF5A1F]/40 rounded-md text-white text-sm py-1.5 px-2 focus:outline-none focus:border-[#FF5A1F]" />
            <select value={tag} onChange={(e) => setTag(e.target.value)}
              data-testid={`vault-rename-tag-${file.id}`}
              className="bg-white/[0.05] border border-white/10 rounded-md text-white text-sm py-1.5 px-2 focus:outline-none focus:border-[#FF5A1F]">
              {tags.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </div>
        ) : (
          <>
            <div className="text-white font-semibold text-sm truncate">{file.label}</div>
            <div className="text-[11px] text-neutral-500 flex items-center gap-2 flex-wrap mt-0.5">
              <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-[#FF5A1F]/15 text-[#FF9764] border border-[#FF5A1F]/30 font-bold">
                {file.tag_label}
              </span>
              <span className="truncate">{file.original_filename}</span>
              <span>·</span>
              <span>{formatBytes(file.size_bytes)}</span>
              <span>·</span>
              <span>{formatDate(file.created_at)}</span>
            </div>
          </>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {editing ? (
          <>
            <IconBtn onClick={save} testid={`vault-rename-save-${file.id}`} title="Save">
              <FloppyDisk size={16} weight="bold" />
            </IconBtn>
            <IconBtn onClick={() => { setEditing(false); setLabel(file.label); setTag(file.tag); }}
              testid={`vault-rename-cancel-${file.id}`} title="Cancel">
              <X size={16} weight="bold" />
            </IconBtn>
          </>
        ) : (
          <>
            <IconBtn onClick={onDownload} testid={`vault-download-${file.id}`} title="Download">
              <Download size={16} weight="bold" />
            </IconBtn>
            <IconBtn onClick={() => setEditing(true)} testid={`vault-edit-${file.id}`} title="Rename">
              <PencilSimple size={16} weight="bold" />
            </IconBtn>
            <IconBtn onClick={onDelete} testid={`vault-delete-${file.id}`} title="Delete" danger>
              <Trash size={16} weight="bold" />
            </IconBtn>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────── Helpers ─────────────────── */
function TagChip({ active, onClick, label, count, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${active
        ? "bg-[#FF5A1F] text-black"
        : "bg-white/[0.04] hover:bg-white/[0.08] text-neutral-300 border border-white/10"}`}>
      {label} <span className={`text-[10px] ${active ? "text-black/70" : "text-neutral-500"}`}>({count})</span>
    </button>
  );
}

function IconBtn({ onClick, children, testid, title, danger }) {
  return (
    <button type="button" onClick={onClick} title={title} data-testid={testid}
      className={`w-9 h-9 rounded-md flex items-center justify-center transition-colors ${danger
        ? "text-neutral-500 hover:bg-red-500/15 hover:text-red-300"
        : "text-neutral-400 hover:bg-white/[0.06] hover:text-white"}`}>
      {children}
    </button>
  );
}

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed border-white/10 py-14 text-center" data-testid="vault-empty">
      <Vault size={40} weight="duotone" className="text-neutral-600 mx-auto mb-3" />
      <div className="text-white font-semibold mb-1">Your vault is empty</div>
      <div className="text-sm text-neutral-500 max-w-md mx-auto">
        Upload your first document above. Most carriers start with their current COI, W-9, and MC authority letter.
      </div>
    </div>
  );
}

function formatBytes(n) {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}
